import {
  ChatMessageWithParts,
  contentTags,
  ContentType,
  TMessageContentPart,
} from "src/views/assistant/types";
import {
  extractToolCallId,
  looksLikeToolCallPayload,
  looksLikeToolResponsePayload,
  parseObjectCandidate,
} from "./contentTypeUtils";

/**
 * Whether two consecutive same-id message parts are fragments of the same
 * logical part (concat) or distinct siblings (keep separate). Sequential
 * streaming fragments concat; a parallel batch persists sibling tool
 * calls/responses as adjacent same-type parts, and fusing distinct
 * tool_call_ids hides every call but the last.
 */
/**
 * Snapshot re-hydration and SSE replay can both deliver the same User
 * message; drop re-inserts by id. User only: Assistant/Tool chunks reuse
 * one id across chunks, so deduping them would drop content.
 */
export function isDuplicateUserMessage(
  dialogue: ChatMessageWithParts[],
  messageType: string | undefined,
  messageId: string | undefined,
): boolean {
  return (
    messageType === "User" &&
    Boolean(messageId) &&
    dialogue.some((msg) => msg.id === messageId)
  );
}

export function canMergeConsecutiveParts(
  prev: TMessageContentPart,
  next: TMessageContentPart,
): boolean {
  if (prev.type !== next.type) return false;
  if (
    next.type !== ContentType.TOOL_CALL &&
    next.type !== ContentType.TOOL_RESPONSE
  ) {
    return true;
  }
  const prevId = extractToolCallId(prev.text);
  const nextId = extractToolCallId(next.text);
  return !prevId || !nextId || prevId === nextId;
}

export const getContentTypeFromTag = (tag: string): ContentType => {
  return contentTags[tag] || ContentType.TEXT;
};

/**
 * Check if a text string is just an orphaned </tool_response> sentinel
 * The backend may send closing tags separately as signal events (see ToolResponse.tsx)
 */
const isToolResponseSentinel = (text: string): boolean => {
  return text.trim() === "</tool_response>";
};

/**
 * Extract a balanced JSON object starting at `pos` in `text`.
 * Uses brace-counting to handle nested objects/arrays and respects strings.
 * Returns the substring if balanced braces are found, otherwise null.
 */
function extractBalancedJson(text: string, pos: number): string | null {
  if (text[pos] !== "{") return null;

  let depth = 0;
  let inString = false;
  let escape = false;

  for (let i = pos; i < text.length; i++) {
    const ch = text[i];

    if (escape) {
      escape = false;
      continue;
    }

    if (ch === "\\" && inString) {
      escape = true;
      continue;
    }

    if (ch === '"') {
      inString = !inString;
      continue;
    }

    if (inString) continue;

    if (ch === "{") depth++;
    else if (ch === "}") {
      depth--;
      if (depth === 0) {
        return text.slice(pos, i + 1);
      }
    }
  }

  return null;
}

/**
 * Scan text for embedded JSON objects that look like tool calls or tool
 * responses. Returns typed content parts splitting the text around recognized
 * JSON payloads.
 *
 * Used as a legacy fallback for old conversations stored before the
 * structured content_parts protocol was introduced.
 */
export function splitJsonToolParts(
  content: string,
): { type: ContentType; text: string }[] | null {
  const parts: { type: ContentType; text: string }[] = [];
  let lastIndex = 0;
  let foundAny = false;

  for (let i = 0; i < content.length; i++) {
    if (content[i] !== "{") continue;

    const jsonStr = extractBalancedJson(content, i);
    if (!jsonStr) continue;

    const parsed = parseObjectCandidate(jsonStr);
    if (!parsed) continue;

    let type: ContentType | null = null;
    // Check response before call — a response with tool_call_id could also
    // have a `name` field, so response detection must come first.
    if (looksLikeToolResponsePayload(parsed)) {
      type = ContentType.TOOL_RESPONSE;
    } else if (looksLikeToolCallPayload(parsed)) {
      type = ContentType.TOOL_CALL;
    }

    if (!type) continue;

    foundAny = true;

    // Push any text before this JSON object
    if (i > lastIndex) {
      const textBefore = content.slice(lastIndex, i);
      if (textBefore.trim()) {
        parts.push({ type: ContentType.TEXT, text: textBefore });
      }
    }

    parts.push({ type, text: jsonStr });
    lastIndex = i + jsonStr.length;
    i = lastIndex - 1; // -1 because loop does i++
  }

  if (!foundAny) return null;

  // Push any remaining text after the last JSON object
  if (lastIndex < content.length) {
    const tail = content.slice(lastIndex);
    if (tail.trim()) {
      parts.push({ type: ContentType.TEXT, text: tail });
    }
  }

  return parts.length > 0 ? parts : null;
}

export const parseMessageContent = (
  content: string,
): { type: ContentType; text: string }[] => {
  const pattern = new RegExp(
    `(<(${Object.keys(contentTags).join("|")})>[\\s\\S]*?<\\/\\2>)`,
    "g",
  );

  const parts: { type: ContentType; text: string }[] = [];
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(content)) !== null) {
    const textBefore = content.slice(lastIndex, match.index);
    if (textBefore) {
      parts.push({ type: ContentType.TEXT, text: textBefore });
    }

    const tag = match[2];
    const type = getContentTypeFromTag(tag);
    parts.push({ type, text: match[0] });
    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < content.length) {
    const tailText = content.slice(lastIndex);
    if (tailText) {
      parts.push({ type: ContentType.TEXT, text: tailText });
    }
  }

  if (parts.length === 0) {
    return [{ type: ContentType.TEXT, text: content }];
  }

  // If XML parsing found only TEXT parts (no XML tags matched), try JSON
  // detection for legacy conversations with embedded JSON tool payloads.
  const allText = parts.every((p) => p.type === ContentType.TEXT);
  if (allText) {
    const jsonParts = splitJsonToolParts(content);
    if (jsonParts) return jsonParts;
  }

  // Filter out TEXT parts that are just orphaned </tool_response> sentinels
  // These shouldn't be rendered as they're backend signal artifacts
  return parts.filter(
    (part) =>
      part.type !== ContentType.TEXT || !isToolResponseSentinel(part.text),
  );
};

/**
 * Protocol enforcement: validate and normalize content parts.
 *
 * Catches TEXT parts whose text is actually JSON tool call / tool response
 * payloads and re-classifies them to the correct ContentType.  Also splits
 * TEXT parts that contain embedded JSON tool payloads mixed with prose.
 *
 * Apply this after building content parts from ANY source (SSE streaming,
 * DB loading, replay) to guarantee the rendering layer never receives raw
 * JSON disguised as plain text.
 */
export function normalizeContentParts(
  parts: TMessageContentPart[],
): TMessageContentPart[] {
  const result: TMessageContentPart[] = [];

  for (const part of parts) {
    // Non-TEXT parts are already classified — pass through
    if (part.type !== ContentType.TEXT) {
      result.push(part);
      continue;
    }

    const trimmed = part.text.trim();

    // Fast path: empty or clearly non-JSON text
    if (!trimmed || !trimmed.includes("{")) {
      result.push(part);
      continue;
    }

    // If the entire text is a single JSON tool payload, reclassify the part
    if (trimmed.startsWith("{")) {
      const parsed = parseObjectCandidate(trimmed);
      if (parsed) {
        // Check response BEFORE call — a response with tool_call_id may also
        // have a `name` field, so response detection must come first.
        if (looksLikeToolResponsePayload(parsed)) {
          result.push({ type: ContentType.TOOL_RESPONSE, text: part.text });
          continue;
        }
        if (looksLikeToolCallPayload(parsed)) {
          result.push({ type: ContentType.TOOL_CALL, text: part.text });
          continue;
        }
      }
    }

    // Text contains `{` but doesn't start with it — may have embedded JSON
    // tool payloads mixed with prose.  Split around recognized payloads.
    const splitParts = splitJsonToolParts(part.text);
    if (splitParts) {
      result.push(...splitParts);
      continue;
    }

    // Genuine text content — pass through
    result.push(part);
  }

  return result;
}
